import { defineAsyncComponent, defineComponent, h } from "vue";
import helpers from "@/helpers/global";
import { safeGetItem, safeSetItem } from "@/utils/storage";
import { isPublicAuthPath, isHiringProductPath } from "@/utils/routeFlags";
import { normalizeLanguageKey, toLanguageStorageValue } from "@/helpers/language";
import ToolboxOverlay from "@/components/ToolboxOverlay.vue";
// PERFORMANCE: These must be registered under names that do NOT match a
// filename in src/components — unplugin-vue-components resolves matching
// template tags to static imports, which would pull Navbar/HelpSupportFab
// (and their element-plus + firebase deps) into the entry bundle and defeat
// defineAsyncComponent.
// Holds the navbar's height while its code loads, so the page below does
// not render at the top and then jump down when the navbar arrives.
const NavbarSpace = {
  render: () => h("div", {
    class: "menu-container",
    style: "background: transparent",
    "aria-hidden": "true"
  })
};
const LazyNavbar = defineAsyncComponent({
  loader: () => import("@/components/Navbar.vue"),
  loadingComponent: NavbarSpace,
  delay: 0
});
const LazyHelpSupportFab = defineAsyncComponent(() => import("@/components/HelpSupportFab.vue"));
const __default__ = defineComponent({
  name: "App",
  components: {
    LazyNavbar,
    LazyHelpSupportFab,
    ToolboxOverlay
  },
  created() {
    if (!isPublicAuthPath(this.$route.path)) {
      this.$store.dispatch("auth/setUserLogin");
    }
    // PERFORMANCE: Only hide body initially for auth routes that need styling
    // Signup/login pages should show immediately
    if (this.$route.meta.auth && !this.$route.path.startsWith("/assessments/candidate")) {
      document.body.style.visibility = "hidden";
    }
  },
  watch: {
    isReady(value) {
      if (value === true) {
        // PERFORMANCE: Skip delay for unauthenticated routes (signup, login, etc.)
        // These pages don't need to wait for user/team data
        const isUnauthRoute = !this.$route.meta.auth;
        if (isUnauthRoute) {
          // Show immediately for signup/login pages
          document.body.style.visibility = "visible";
          return;
        }
        // Keep minimal delay only for authenticated routes that need styling
        setTimeout(() => {
          try {
            const app = document.getElementById("app");
            let style = app.firstChild ? app.firstChild.getAttribute("style") : "";
            document.body.setAttribute("style", style);
            // Reduced delay from 600ms to 100ms
            setTimeout(() => {
              document.body.style.visibility = "visible";
            }, 100);
          } catch (e) {
            document.body.style.visibility = "visible";
          }
        }, 50); // Reduced from 200ms to 50ms
      }
    },
    "$i18n.locale"(newLocale) {
      this.updateHtmlLang(newLocale);
    },
    companyColor: {
      immediate: true,
      handler(color) {
        if (color) {
          document.documentElement.style.setProperty("--app-primary-color", color);
        }
      }
    },
    fontColor: {
      immediate: true,
      handler(color) {
        if (color) {
          document.documentElement.style.setProperty("--app-primary-font-color", color);
        }
      }
    }
  },
  computed: {
    customHostBootBlocked() {
      return this.$store.getters["customHost/bootBlocked"];
    },
    customHostBootKind() {
      return this.$store.getters["customHost/bootBlockKind"];
    },
    customHostBootTitle() {
      const k = this.customHostBootKind;
      if (k === "inactive") return String(this.$t("customDomain.boot.inactiveTitle"));
      if (k === "error") return String(this.$t("customDomain.boot.errorTitle"));
      return String(this.$t("customDomain.boot.notConfiguredTitle"));
    },
    customHostBootBody() {
      const k = this.customHostBootKind;
      if (k === "inactive") return String(this.$t("customDomain.boot.inactiveBody"));
      if (k === "error") return String(this.$t("customDomain.boot.errorBody"));
      return String(this.$t("customDomain.boot.notConfiguredBody"));
    },
    isReady() {
      return this.$store.getters["isReady"];
    },
    isToolboxRoute() {
      return this.$route.meta.toolbox === true;
    },
    showFab() {
      // Show FAB on authenticated admin pages
      // Don't show on login/signup pages or candidate-facing pages
      // Check if route requires authentication (admin pages)
      const isAuthRoute = this.$route.meta.auth;
      // Exclude specific auth and candidate routes
      const excludedRoutes = [
      // Auth routes
      "Login", "Login_th", "Signup", "Signup_th", "SignupVerify", "AccountVerify", "SignupInfo", "SignupBranding", "Pincode",
      // Candidate routes
      "ConfirmCanditate", "AssessmentTest", "AssessmentTestForCandidate", "AssessmentTestForCandidateSecure", "AssessmentResults", "AssessmentResultsTH", "AssessmentResultsEN", "AssessmentsCandidate"];
      const isExcludedRoute = excludedRoutes.includes(String(this.$route.name));
      // Also exclude routes with candidate or test paths
      const isCandidatePath = this.$route.path.includes("/assessment-test") || this.$route.path.includes("/confirm-canditate") || this.$route.path.includes("/assessment-results") || this.$route.path.includes("/assessments/candidate");
      return isAuthRoute && !isExcludedRoute && !isCandidatePath;
    },
    companyColor() {
      var _binding$publicBrandi;
      if (isHiringProductPath(this.$route.path) || /^\/signup(?:\/|$)/.test(this.$route.path)) {
        // Keep product and signup controls neutral, including with cached branding.
        // Candidate and report branding stays in its explicit brand components.
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.fontColor = "#fff";
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.iconColor = "";
        return "#1B1C1E";
      }
      const defaultCompanyColor = "#1B1C1E";
      const binding = this.$store.getters["customHost/binding"];
      const user = this.$store.getters["user/user"];
      const team = this.$store.getters["team/team"];
      const userRoute = this.$store.getters["routes/user"];
      const isCandidatePath = this.$route.path.includes("/assessment-test") || this.$route.path.includes("/confirm-canditate") || this.$route.path.includes("/assessment-results") || this.$route.path.includes("/assessments/candidate");
      if (binding !== null && binding !== void 0 && (_binding$publicBrandi = binding.publicBranding) !== null && _binding$publicBrandi !== void 0 && _binding$publicBrandi.color && !(team && !team.personal && team.color)) {
        const color = String(binding.publicBranding.color);
        const check = helpers.isLightColor(color);
        if (check) {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.fontColor = "#000000";
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.iconColor = "invert(1)";
        } else {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.fontColor = "#fff";
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.iconColor = "";
        }
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        safeSetItem("cachedCompanyColor", color);
        return color;
      }
      if (user || team) {
        let color = defaultCompanyColor;
        if (team && !team.personal) {
          color = team.color ? team.color : defaultCompanyColor;
        } else if (isCandidatePath && userRoute && userRoute.color) {
          color = userRoute.color;
        } else if (user && user.color) {
          color = user.color;
        }
        const check = helpers.isLightColor(color);
        if (check) {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.fontColor = "#000000";
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.iconColor = "invert(1)";
        } else {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.fontColor = "#fff";
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.iconColor = "";
        }
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        safeSetItem("cachedCompanyColor", color);
        return color;
      } else if (userRoute && userRoute.color) {
        const check = helpers.isLightColor(userRoute.color);
        if (check) {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.fontColor = "#000000";
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.iconColor = "invert(1)";
        } else {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.fontColor = "#fff";
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.iconColor = "";
        }
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        safeSetItem("cachedCompanyColor", userRoute.color);
        return userRoute.color;
      } else {
        // Use cached color from previous session to prevent flash on reload
        const cached = safeGetItem("cachedCompanyColor");
        if (cached) {
          const check = helpers.isLightColor(cached);
          if (check) {
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.fontColor = "#000000";
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.iconColor = "invert(1)";
          } else {
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.fontColor = "#fff";
          }
          return cached;
        }
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.fontColor = "#fff";
      }
      return defaultCompanyColor;
    }
  },
  data() {
    return {
      fontColor: "",
      iconColor: ""
    };
  },
  mounted() {
    this.setLang();
  },
  methods: {
    setLang() {
      const storedLang = String(safeGetItem("lang") || "");
      const lang = normalizeLanguageKey(storedLang);
      this.$i18n.setLocale(lang);
      this.updateHtmlLang(lang);
      safeSetItem("lang", toLanguageStorageValue(lang));
    },
    updateHtmlLang(locale) {
      document.documentElement.setAttribute("lang", locale);
    },
    closeToolbox() {
      this.$router.push({
        name: "Assessments"
      });
    }
  }
});
import { useCssVars as _useCssVars } from 'vue';
const __injectCSSVars__ = () => {
  _useCssVars(_ctx => ({
    "26634af2": _ctx.companyColor,
    "f8ec735a": _ctx.fontColor,
    "e71bbf2e": _ctx.iconColor
  }));
};
const __setup__ = __default__.setup;
__default__.setup = __setup__ ? (props, ctx) => {
  __injectCSSVars__();
  return __setup__(props, ctx);
} : __injectCSSVars__;
export default __default__;